import { ReactNode } from 'react';
import { PanelCard, Chip, Track } from '../../components/ui';
import RingProgress from '../../components/charts/RingProgress';
import { useClassPerfOverview, useClassPerfSectionRanks, useClassPerfTrends, useStudentsList } from '../../api/hooks';
import { formatPtsDelta, ptsTone } from '../class_performance/lib/overviewMath';
import { shortSection } from '../class_performance/lib/cpBits';
import { activityCounts, ordinal, passRate, trendChange } from './sectionMath';
import { PASS_MARK } from '../../lib/tableRules';

interface Props { gradeId: number; sectionId: number; gradeName: string }

const WEEKS = 8;
const LABEL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

/** Design `.card.kpi`: a label (and chip) row, a 38px mono value and whatever follows. */
function Kpi({ label, chip, children }: { label: string; chip?: ReactNode; children: ReactNode }) {
  return (
    <PanelCard>
      <div className="flex min-h-[24px] items-center justify-between gap-2">
        <span className={LABEL}>{label}</span>
        {chip}
      </div>
      {children}
    </PanelCard>
  );
}

const Value = ({ children }: { children: ReactNode }) => (
  <div className="mt-2 font-mono text-[38px] font-semibold leading-none tracking-[-0.04em]">{children}</div>
);
const Unit = ({ children, muted = false }: { children: ReactNode; muted?: boolean }) => (
  <span className={`text-lg ${muted ? 'text-muted' : ''}`}>{children}</span>
);

/** Area + line of the weekly averages, stretched to the card width (design 220x48 box). */
function Spark({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const lo = Math.min(...values), hi = Math.max(...values);
  const x = (i: number) => (i * 220) / (values.length - 1);
  const y = (v: number) => 44 - ((v - lo) / (hi === lo ? 1 : hi - lo)) * 38;
  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 220 48" preserveAspectRatio="none" aria-hidden className="mt-1.5 block h-12 w-full">
      <path d={`${line} L220,48 L0,48 Z`} className="fill-slate" fillOpacity={0.14} />
      <path d={line} fill="none" className="stroke-slate" strokeWidth={2.2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default function SectionKpis({ gradeId, sectionId, gradeName }: Props) {
  const section = useClassPerfOverview({ grade: gradeId, section: sectionId, subject: 'all' });
  const grade = useClassPerfOverview({ grade: gradeId, section: 'all', subject: 'all' });
  const trend = useClassPerfTrends({ grade: gradeId, section: sectionId, subject: 'all', weeks: WEEKS });
  const roster = useStudentsList({ grade: gradeId, section: sectionId });
  const ranks = useClassPerfSectionRanks({ grade: gradeId });

  const s = section.data;
  const delta = s?.avg_score != null && grade.data?.avg_score != null ? s.avg_score - grade.data.avg_score : null;
  const series = trend.data?.series ?? [];
  const change = trendChange(series);
  const pass = s ? passRate(s.histogram, PASS_MARK) : null;
  const rows = roster.data?.results ?? [];
  const act = roster.data ? activityCounts(rows) : null;

  const rankRows = (ranks.data?.sections ?? []).filter((r) => r.avg_score != null && r.rank != null)
    .sort((a, b) => (a.rank as number) - (b.rank as number));
  const mine = rankRows.find((r) => r.id === sectionId);

  const avgFoot = [
    change ? `${change.change >= 0 ? 'Up' : 'Down'} ${Math.abs(change.change).toFixed(1)} pts over ${change.weeks} weeks` : null,
    grade.data?.avg_score != null ? `${gradeName} is ${grade.data.avg_score.toFixed(1)}%` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="grid grid-cols-1 gap-[18px] min-[641px]:grid-cols-2">
      <Kpi label="Section average"
        chip={delta != null ? <Chip tone={ptsTone(delta)}>{formatPtsDelta(delta)} vs grade</Chip> : undefined}>
        <Value>{s?.avg_score != null ? <>{s.avg_score.toFixed(1)}<Unit>%</Unit></> : '—'}</Value>
        <Spark values={series.map((p) => p.avg_score).filter((v): v is number => v != null)} />
        {avgFoot && <span className="mt-1.5 block text-xs text-muted">{avgFoot}</span>}
      </Kpi>

      <Kpi label="Pass rate">
        <div className="flex items-center justify-between gap-3">
          <Value>{pass?.pct != null ? <>{Math.round(pass.pct)}<Unit>%</Unit></> : '—'}</Value>
          {pass?.pct != null && <RingProgress value={pass.pct} label="Pass rate" />}
        </div>
        {pass && pass.total > 0 && (
          <span className="mt-1.5 block text-xs text-muted">{pass.passing} of {pass.total} students at or above {PASS_MARK}%</span>
        )}
      </Kpi>

      <Kpi label={`Rank in ${gradeName}`}
        chip={mine && rankRows.length > 1 && mine.rank === rankRows.length ? <Chip tone="accent">Needs a push</Chip> : undefined}>
        <Value>
          {mine ? <>{mine.rank}<Unit>{ordinal(mine.rank as number).replace(/^\d+/, '')}</Unit><Unit muted>{` of ${rankRows.length}`}</Unit></> : '—'}
        </Value>
        {mine ? (
          <div className="mt-2.5 flex flex-col gap-[7px]">
            {rankRows.map((r) => (
              <div key={r.id} className="flex items-center gap-2.5">
                <span className="w-[26px] font-mono text-xs font-medium">{shortSection(gradeName, r.name) || r.name}</span>
                <Track value={r.avg_score as number} tone={r.id === sectionId ? 'accent' : 'slate'} label={`${r.display} average`} className="flex-1" />
                <span className="w-10 text-right font-mono text-xs font-medium">{(r.avg_score as number).toFixed(1)}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className="mt-2.5 block text-xs text-muted">{ranks.isLoading ? 'Ranking sections…' : 'Section ranking is not available yet.'}</span>
        )}
      </Kpi>

      <Kpi label="Active students">
        <Value>
          {act ? <>{act.active}<Unit muted>{` / ${rows.length}`}</Unit></> : '—'}
        </Value>
        {act && rows.length > 0 && (
          <>
            <Track value={(act.active / rows.length) * 100} tone="slate" label="Students active in the last 2 days" className="mt-3.5" />
            <span className="mt-1.5 block text-xs text-muted">
              {act.idle} idle, {act.silent} silent for 7+ days · {act.openAlerts} open {act.openAlerts === 1 ? 'alert' : 'alerts'}
            </span>
          </>
        )}
      </Kpi>
    </div>
  );
}
